import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Mic, Lock, CreditCard, Activity, Settings, RefreshCw,
  CheckCircle, Clock, Zap, ChevronRight, Code2,
  PhoneCall, AlertCircle, FileDown, Key, Plus, RotateCcw,
  Trash2, Copy, Eye, EyeOff, Shield, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusBadge = (status) => {
  const map = {
    active:      { color: 'bg-emerald-100 text-emerald-700', label: 'Active' },
    expired:     { color: 'bg-slate-100 text-slate-500',     label: 'Expired' },
    locked:      { color: 'bg-blue-100 text-blue-700',       label: 'Locked' },
    consumed:    { color: 'bg-purple-100 text-purple-700',   label: 'Consumed' },
    paid:        { color: 'bg-green-100 text-green-700',     label: 'Paid' },
    failed:      { color: 'bg-red-100 text-red-700',         label: 'Failed' },
    cancelled:   { color: 'bg-orange-100 text-orange-700',   label: 'Cancelled' },
    paid_conflict:{ color: 'bg-amber-100 text-amber-700',    label: 'Conflict' },
  };
  const s = map[status] || { color: 'bg-slate-100 text-slate-600', label: status || '—' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return iso; }
};

// ── KPI Card ──────────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, color }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-bold text-slate-800 mt-1">{value ?? '—'}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
const DashboardTab = ({ data, loading, onRefresh }) => {
  if (loading) return <p className="text-slate-500 py-8 text-center">Loading…</p>;
  if (!data)   return <p className="text-slate-400 py-8 text-center">No data yet.</p>;

  const { stats, recent_enquiries = [], recent_locks = [], recent_payment_links = [] } = data;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard icon={Mic}        label="Active Enquiries"    value={stats.active_enquiries}    color="bg-blue-50 text-blue-600" />
        <KpiCard icon={Activity}   label="Total Enquiries"     value={stats.total_enquiries}     color="bg-indigo-50 text-indigo-600" />
        <KpiCard icon={Lock}       label="Active Locks"        value={stats.active_locks}        color="bg-amber-50 text-amber-600" />
        <KpiCard icon={Clock}      label="Total Locks"         value={stats.total_locks}         color="bg-orange-50 text-orange-600" />
        <KpiCard icon={CreditCard} label="Active Pay Links"    value={stats.active_payment_links} color="bg-emerald-50 text-emerald-600" />
        <KpiCard icon={CheckCircle} label="Paid Links"         value={stats.paid_count}          color="bg-green-50 text-green-600" />
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onRefresh} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Recent Enquiries */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Mic className="w-4 h-4 text-blue-500" /> Recent Enquiries
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="enquiries-table">
              <thead>
                <tr className="border-b text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 text-left font-medium">Enquiry ID</th>
                  <th className="py-2 text-left font-medium">Sport</th>
                  <th className="py-2 text-left font-medium">Location</th>
                  <th className="py-2 text-left font-medium">Date</th>
                  <th className="py-2 text-left font-medium">Time</th>
                  <th className="py-2 text-left font-medium">Mobile</th>
                  <th className="py-2 text-left font-medium">Available</th>
                  <th className="py-2 text-left font-medium">Status</th>
                  <th className="py-2 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {recent_enquiries.length === 0 ? (
                  <tr><td colSpan={9} className="py-6 text-center text-slate-400">No enquiries yet</td></tr>
                ) : recent_enquiries.map((e, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="py-2 font-mono text-xs text-slate-600">{e.enquiry_id}</td>
                    <td className="py-2">{e.sport}</td>
                    <td className="py-2">{e.location}</td>
                    <td className="py-2">{e.date}</td>
                    <td className="py-2">{e.start_time}–{e.end_time}</td>
                    <td className="py-2 font-mono text-xs">{e.player_mobile_number}</td>
                    <td className="py-2">
                      {e.available
                        ? <span className="text-emerald-600 font-medium">YES</span>
                        : <span className="text-red-500 font-medium">NO</span>}
                    </td>
                    <td className="py-2">{statusBadge(e.status)}</td>
                    <td className="py-2 text-xs text-slate-400">{fmtTime(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Locks */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Lock className="w-4 h-4 text-amber-500" /> Recent Slot Locks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="locks-table">
              <thead>
                <tr className="border-b text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 text-left font-medium">Lock ID</th>
                  <th className="py-2 text-left font-medium">Court</th>
                  <th className="py-2 text-left font-medium">Venue</th>
                  <th className="py-2 text-left font-medium">Date / Time</th>
                  <th className="py-2 text-left font-medium">Mobile</th>
                  <th className="py-2 text-left font-medium">Amount</th>
                  <th className="py-2 text-left font-medium">Status</th>
                  <th className="py-2 text-left font-medium">Expires</th>
                </tr>
              </thead>
              <tbody>
                {recent_locks.length === 0 ? (
                  <tr><td colSpan={8} className="py-6 text-center text-slate-400">No slot locks yet</td></tr>
                ) : recent_locks.map((l, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="py-2 font-mono text-xs text-slate-600">{l.lock_id}</td>
                    <td className="py-2">{l.court_name}</td>
                    <td className="py-2">{l.venue_name}</td>
                    <td className="py-2 text-xs">{l.date} {l.start_time}–{l.end_time}</td>
                    <td className="py-2 font-mono text-xs">{l.player_mobile_number}</td>
                    <td className="py-2">₹{l.amount || '—'}</td>
                    <td className="py-2">{statusBadge(l.status)}</td>
                    <td className="py-2 text-xs text-slate-400">{fmtTime(l.expires_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Payment Links */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-emerald-500" /> Recent Payment Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payment-links-table">
              <thead>
                <tr className="border-b text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 text-left font-medium">Link ID</th>
                  <th className="py-2 text-left font-medium">Lock ID</th>
                  <th className="py-2 text-left font-medium">Amount</th>
                  <th className="py-2 text-left font-medium">Cashfree Order</th>
                  <th className="py-2 text-left font-medium">Status</th>
                  <th className="py-2 text-left font-medium">WABA</th>
                  <th className="py-2 text-left font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {recent_payment_links.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-slate-400">No payment links yet</td></tr>
                ) : recent_payment_links.map((p, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="py-2 font-mono text-xs text-slate-600">{p.payment_link_id}</td>
                    <td className="py-2 font-mono text-xs">{p.lock_id}</td>
                    <td className="py-2 font-semibold">₹{p.amount}</td>
                    <td className="py-2 font-mono text-xs">{p.cashfree_order_id}</td>
                    <td className="py-2">{statusBadge(p.status)}</td>
                    <td className="py-2">
                      {p.waba_status === 'sent'
                        ? <span className="text-emerald-600 text-xs font-medium">Sent</span>
                        : p.waba_status === 'failed'
                        ? <span className="text-red-500 text-xs font-medium">Failed</span>
                        : <span className="text-slate-400 text-xs">—</span>}
                    </td>
                    <td className="py-2 text-xs text-slate-400">{fmtTime(p.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ── Settings Tab ──────────────────────────────────────────────────────────────
const SettingsTab = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/voice/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(r.data);
    } catch { toast.error('Failed to load settings'); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/voice/settings`, settings, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  if (!settings) return <p className="text-slate-400 py-8 text-center">Loading…</p>;

  const fields = [
    { key: 'enquiry_validity_minutes',  label: 'Enquiry Validity (minutes)',    hint: 'How long an availability enquiry remains valid before expiring.' },
    { key: 'lock_duration_minutes',     label: 'Slot Lock Duration (minutes)',  hint: 'How long a locked slot is held before being released automatically.' },
    { key: 'payment_expiry_minutes',    label: 'Payment Link Expiry (minutes)', hint: 'Time window for the player to complete payment after link is sent.' },
    { key: 'waba_retry_limit',          label: 'WABA Retry Limit',              hint: 'Max number of times to retry a failed WhatsApp send.' },
    { key: 'waba_retry_interval_secs',  label: 'WABA Retry Interval (seconds)', hint: 'Wait time between retries.' },
    { key: 'cleanup_interval_minutes',  label: 'Cleanup Interval (minutes)',    hint: 'How often stale/expired records are purged from the database.' },
  ];

  return (
    <Card className="border-0 shadow-sm max-w-2xl">
      <CardHeader>
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Settings className="w-4 h-4 text-slate-500" /> Voice API Configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {fields.map(({ key, label, hint }) => (
          <div key={key} data-testid={`voice-setting-${key}`}>
            <Label className="text-sm font-medium text-slate-700">{label}</Label>
            <Input
              type="number"
              min={1}
              className="mt-1 w-40"
              value={settings[key] ?? ''}
              onChange={e => setSettings(s => ({ ...s, [key]: parseInt(e.target.value, 10) || 1 }))}
            />
            <p className="text-xs text-slate-400 mt-1">{hint}</p>
          </div>
        ))}

        <div className="pt-2">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-voice-settings-btn"
            className="gap-2"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </div>

        <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">WABA Template Required</p>
              <p className="text-xs text-amber-700 mt-1">
                The Voice AI module uses the <code className="font-mono bg-amber-100 px-1 rounded">sendpaymentlinkvoice</code> WABA template.
                Ensure this template is approved in your Meta Business Manager before going live.
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ── API Keys Tab ──────────────────────────────────────────────────────────────

const ENVS = ['production', 'test', 'dev'];
const PERMS = ['enquiry', 'lock', 'payment'];
const envColor = { production: 'bg-green-100 text-green-700', test: 'bg-blue-100 text-blue-700', dev: 'bg-slate-100 text-slate-600' };

const KeyRevealModal = ({ keyData, onClose }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(keyData.api_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-5 h-5" /> Save Your API Key Now
          </DialogTitle>
          <DialogDescription>
            This is the <strong>only time</strong> this key will be shown. Copy it and store it securely.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
            <p className="text-xs text-amber-700 font-semibold mb-2 uppercase tracking-wide">API Key</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono text-slate-800 break-all bg-white px-3 py-2 rounded border border-amber-200" data-testid="revealed-key">
                {keyData.api_key}
              </code>
              <Button size="sm" variant="outline" onClick={copy} className="shrink-0 gap-1.5" data-testid="copy-key-btn">
                {copied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-slate-500">Name:</span> <span className="font-medium">{keyData.name}</span></div>
            <div><span className="text-slate-500">Environment:</span> <span className="font-medium capitalize">{keyData.environment}</span></div>
            <div><span className="text-slate-500">Permissions:</span> <span className="font-medium">{(keyData.permissions || []).join(', ')}</span></div>
            <div><span className="text-slate-500">Rate Limit:</span> <span className="font-medium">{keyData.rate_limit_per_minute} req/min</span></div>
          </div>
          <div className="p-3 bg-red-50 rounded border border-red-200 text-xs text-red-700 flex gap-2">
            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
            <span>Never commit this key to source code. Never pass it in URL parameters. Store it in a secrets manager or environment variable.</span>
          </div>
          <Button onClick={onClose} className="w-full" data-testid="confirm-saved-btn">I have saved this key</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CreateKeyModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '', environment: 'production',
    permissions: ['enquiry', 'lock', 'payment'],
    rate_limit_per_minute: 60, notes: ''
  });
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const togglePerm = (p) => setForm(f => ({
    ...f,
    permissions: f.permissions.includes(p)
      ? f.permissions.filter(x => x !== p)
      : [...f.permissions, p]
  }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.permissions.length) { toast.error('Select at least one permission'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/voice/admin/api-keys`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      onCreated(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create key');
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="w-4 h-4" /> Generate New API Key</DialogTitle>
          <DialogDescription>Create a scoped API key for Voice AI integration.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Key Name <span className="text-red-500">*</span></Label>
            <Input className="mt-1" placeholder="e.g. MAKS Voice AI Production" data-testid="key-name-input"
              value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <Label>Environment</Label>
            <div className="flex gap-2 mt-1">
              {ENVS.map(e => (
                <button key={e} onClick={() => setForm(f => ({ ...f, environment: e }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${form.environment === e ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Permissions</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {PERMS.map(p => (
                <button key={p} onClick={() => togglePerm(p)}
                  data-testid={`perm-${p}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${form.permissions.includes(p) ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                  {p}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">Select only the operations this key needs (least privilege).</p>
          </div>
          <div>
            <Label>Rate Limit (requests / minute)</Label>
            <Input type="number" min={1} max={600} className="mt-1 w-32" data-testid="rate-limit-input"
              value={form.rate_limit_per_minute}
              onChange={e => setForm(f => ({ ...f, rate_limit_per_minute: parseInt(e.target.value) || 60 }))} />
          </div>
          <div>
            <Label>Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input className="mt-1" placeholder="Purpose, team, etc." value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="flex-1 gap-2" data-testid="generate-key-btn">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
              {saving ? 'Generating…' : 'Generate Key'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ApiKeysTab = () => {
  const [keys, setKeys] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [revealData, setRevealData] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [rotating, setRotating] = useState(null);
  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kr, ar] = await Promise.all([
        axios.get(`${API}/api/voice/admin/api-keys`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/voice/admin/audit-logs?limit=50`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setKeys(kr.data.api_keys || []);
      setAuditLogs(ar.data.audit_logs || []);
    } catch { toast.error('Failed to load API keys'); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (data) => {
    setShowCreate(false);
    setRevealData(data);
    load();
  };

  const handleRevoke = async (keyId) => {
    try {
      await axios.delete(`${API}/api/voice/admin/api-keys/${keyId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('API key revoked immediately');
      setRevokeTarget(null);
      load();
    } catch { toast.error('Revoke failed'); }
  };

  const handleRotate = async (keyId) => {
    setRotating(keyId);
    try {
      const r = await axios.post(`${API}/api/voice/admin/api-keys/${keyId}/rotate`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setRevealData({ ...r.data, name: keys.find(k => k.key_id === keyId)?.name || '', environment: '' });
      load();
    } catch { toast.error('Rotate failed'); }
    setRotating(null);
  };

  const statusColor = (k) => k.is_active
    ? 'bg-emerald-100 text-emerald-700'
    : 'bg-red-100 text-red-600';

  return (
    <div className="space-y-6" data-testid="api-keys-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Key className="w-5 h-5 text-indigo-500" />
          <div>
            <p className="font-semibold text-slate-800">Voice AI API Keys</p>
            <p className="text-xs text-slate-500">Keys enforce authentication on all 3 public Voice API endpoints.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5" data-testid="refresh-keys-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5" data-testid="create-key-btn">
            <Plus className="w-4 h-4" /> Generate Key
          </Button>
        </div>
      </div>

      {/* Security notice */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex gap-3 text-sm">
        <Shield className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
        <span className="text-slate-600">All calls to <code className="font-mono text-xs bg-slate-100 px-1 rounded">POST /enquiries</code>, <code className="font-mono text-xs bg-slate-100 px-1 rounded">POST /slots/lock</code>, and <code className="font-mono text-xs bg-slate-100 px-1 rounded">POST /payment-links</code> require a valid <code className="font-mono text-xs bg-slate-100 px-1 rounded">X-API-Key</code> header. Keys are stored as SHA-256 hashes — plaintext is never persisted.</span>
      </div>

      {/* Keys table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="api-keys-table">
              <thead>
                <tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-3 px-4 text-left font-medium">Name / ID</th>
                  <th className="py-3 px-4 text-left font-medium">Env</th>
                  <th className="py-3 px-4 text-left font-medium">Permissions</th>
                  <th className="py-3 px-4 text-left font-medium">Rate Limit</th>
                  <th className="py-3 px-4 text-left font-medium">Total Calls</th>
                  <th className="py-3 px-4 text-left font-medium">Last Used</th>
                  <th className="py-3 px-4 text-left font-medium">Status</th>
                  <th className="py-3 px-4 text-left font-medium">Key Preview</th>
                  <th className="py-3 px-4 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keys.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400">
                    {loading ? 'Loading…' : 'No API keys yet. Generate your first key above.'}
                  </td></tr>
                ) : keys.map(k => (
                  <tr key={k.key_id} className="border-b hover:bg-slate-50" data-testid={`key-row-${k.key_id}`}>
                    <td className="py-3 px-4">
                      <p className="font-medium text-slate-800">{k.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{k.key_id}</p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${envColor[k.environment] || 'bg-slate-100 text-slate-600'}`}>
                        {k.environment}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1 flex-wrap">
                        {(k.permissions || []).map(p => (
                          <span key={p} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded text-xs font-medium">{p}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-600">{k.rate_limit_per_minute}/min</td>
                    <td className="py-3 px-4 font-mono text-slate-700">{k.total_calls || 0}</td>
                    <td className="py-3 px-4 text-xs text-slate-400">{k.last_used_at ? fmtTime(k.last_used_at) : 'Never'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(k)}`}>
                        {k.is_active ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-slate-500">{k.key_preview}</td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        {k.is_active && (
                          <button
                            onClick={() => handleRotate(k.key_id)}
                            disabled={rotating === k.key_id}
                            title="Rotate key"
                            data-testid={`rotate-btn-${k.key_id}`}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                          >
                            {rotating === k.key_id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                          </button>
                        )}
                        {k.is_active && (
                          <button
                            onClick={() => setRevokeTarget(k)}
                            title="Revoke key"
                            data-testid={`revoke-btn-${k.key_id}`}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Activity className="w-4 h-4 text-violet-500" /> Audit Log
            <span className="text-xs font-normal text-slate-400 ml-1">(last 50 calls)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-logs-table">
              <thead>
                <tr className="border-b text-xs text-slate-500 uppercase tracking-wide">
                  <th className="py-2 text-left font-medium">Timestamp</th>
                  <th className="py-2 text-left font-medium">Key</th>
                  <th className="py-2 text-left font-medium">Endpoint</th>
                  <th className="py-2 text-left font-medium">IP</th>
                  <th className="py-2 text-left font-medium">Status</th>
                  <th className="py-2 text-left font-medium">Code</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr><td colSpan={6} className="py-6 text-center text-slate-400">No audit logs yet</td></tr>
                ) : auditLogs.map((l, i) => (
                  <tr key={i} className="border-b hover:bg-slate-50">
                    <td className="py-2 text-xs text-slate-400">{fmtTime(l.timestamp)}</td>
                    <td className="py-2 text-xs">
                      <span className="font-medium text-slate-700">{l.key_name}</span>
                      <span className="text-slate-400 ml-1 font-mono">({l.key_id})</span>
                    </td>
                    <td className="py-2 font-mono text-xs text-slate-600">{l.method} {l.endpoint}</td>
                    <td className="py-2 font-mono text-xs text-slate-400">{l.ip}</td>
                    <td className="py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        l.status === 'accepted' ? 'bg-emerald-50 text-emerald-600' :
                        l.status === 'rate_limited' ? 'bg-orange-50 text-orange-600' :
                        'bg-red-50 text-red-600'
                      }`}>{l.status}</span>
                    </td>
                    <td className="py-2 font-mono text-xs text-slate-500">{l.response_code}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modals */}
      {showCreate && <CreateKeyModal onClose={() => setShowCreate(false)} onCreated={handleCreated} />}
      {revealData && <KeyRevealModal keyData={revealData} onClose={() => setRevealData(null)} />}

      {/* Revoke confirm dialog */}
      {revokeTarget && (
        <Dialog open onOpenChange={() => setRevokeTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-red-600 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> Revoke API Key?
              </DialogTitle>
              <DialogDescription>
                Revoking <strong>{revokeTarget.name}</strong> will immediately reject all requests using this key. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setRevokeTarget(null)} className="flex-1">Cancel</Button>
              <Button variant="destructive" onClick={() => handleRevoke(revokeTarget.key_id)} className="flex-1 gap-2" data-testid="confirm-revoke-btn">
                <Trash2 className="w-4 h-4" /> Revoke Now
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};



const endpointColor = { POST: 'bg-blue-600', GET: 'bg-emerald-600' };

const EndpointCard = ({ method, path, title, desc, request, response, note }) => (
  <Card className="border border-slate-200 shadow-none">
    <CardContent className="p-5">
      <div className="flex items-start gap-3 mb-3">
        <span className={`px-2 py-1 rounded text-xs font-bold text-white ${endpointColor[method]}`}>{method}</span>
        <code className="text-sm font-mono text-slate-700 mt-0.5">{path}</code>
      </div>
      <p className="font-semibold text-slate-800 text-sm mb-1">{title}</p>
      <p className="text-xs text-slate-500 mb-4">{desc}</p>

      {request && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Request Body</p>
          <pre className="text-xs bg-slate-900 text-green-300 rounded-lg p-3 overflow-x-auto">{request}</pre>
        </div>
      )}
      {response && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Response</p>
          <pre className="text-xs bg-slate-900 text-blue-300 rounded-lg p-3 overflow-x-auto">{response}</pre>
        </div>
      )}
      {note && (
        <div className="mt-2 p-2 bg-slate-50 rounded text-xs text-slate-500 border border-slate-200">
          <strong>Note:</strong> {note}
        </div>
      )}
    </CardContent>
  </Card>
);

const ApiReferenceTab = () => {
  const baseUrl = `${API}/api/voice`;

  const downloadGuide = () => {
    window.open(`${API}/api/voice/guide`, '_blank');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex gap-3 flex-1 mr-4">
          <Code2 className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-800">Base URL</p>
            <code className="text-sm font-mono text-blue-700 break-all" data-testid="api-base-url">{baseUrl}</code>
            <p className="text-xs text-blue-600 mt-1">No authentication required for <code>POST /enquiries</code>, <code>POST /slots/lock</code>, and <code>POST /payment-links</code>. All admin endpoints require <code>Authorization: Bearer &lt;token&gt;</code>.</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={downloadGuide} className="gap-2 shrink-0" data-testid="download-guide-btn">
          <FileDown className="w-4 h-4" />
          Download Guide
        </Button>
      </div>

      <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 pt-2">
        <PhoneCall className="w-4 h-4" />
        <span>Step 1 — Check Availability</span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <span className="text-slate-400 font-normal">Voice AI calls this first to get an enquiry_id</span>
      </div>

      <EndpointCard
        method="POST" path="/api/voice/enquiries"
        title="Create Availability Enquiry"
        desc="Checks live court availability for a sport/location/time. Returns enquiry_id (valid for 3 min by default) and available YES/NO."
        request={`{
  "sport": "Pickleball",
  "location": "Bhayandar",
  "date": "2026-02-28",
  "start_time": "6:00 PM",
  "duration_minutes": 60,
  "player_mobile_number": "9876543210"
}`}
        response={`{
  "enquiry_id": "ENQ-ABCD1234",
  "available": true,
  "answer": "YES",
  "duration_minutes": 60,
  "expires_at": "2026-02-28T12:33:00Z",
  "message": "A court is available for your requested time."
}`}
        note="start_time accepts 12h (6:00 PM) or 24h (18:00). Expired enquiries automatically become unusable."
      />

      <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 pt-2">
        <Lock className="w-4 h-4" />
        <span>Step 2 — Lock Slot</span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <span className="text-slate-400 font-normal">Only after player confirms interest</span>
      </div>

      <EndpointCard
        method="POST" path="/api/voice/slots/lock"
        title="Lock a Slot"
        desc="Rechecks live availability and atomically locks the court for 15 min (configurable). Returns lock_id. If the original court is taken, auto-tries the next available court at the same venue."
        request={`{
  "enquiry_id": "ENQ-ABCD1234",
  "command": "Lock"
}`}
        response={`{
  "enquiry_id": "ENQ-ABCD1234",
  "locked": true,
  "lock_id": "LCK-XYZ78910",
  "court": "Court 1",
  "venue": "MAKS Bhayandar",
  "date": "2026-02-28",
  "start_time": "18:00",
  "end_time": "19:00",
  "lock_expires_at": "2026-02-28T12:48:00Z",
  "message": "Slot locked successfully."
}`}
        note="Idempotent — duplicate requests with the same enquiry_id return the existing lock. Concurrent lock attempts return CONCURRENT_LOCK reason."
      />

      <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 pt-2">
        <CreditCard className="w-4 h-4" />
        <span>Step 3 — Generate Payment Link</span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
        <span className="text-slate-400 font-normal">Cashfree link + WABA notification</span>
      </div>

      <EndpointCard
        method="POST" path="/api/voice/payment-links"
        title="Generate Payment Link"
        desc="Creates a Cashfree payment order, stores the link, and fires a WhatsApp message to the player using the sendpaymentlinkvoice template. Idempotent — returns existing link if called again."
        request={`{
  "enquiry_id": "ENQ-ABCD1234",
  "player_name": "Rahul Sharma",
  "player_email": "rahul@example.com"
}`}
        response={`{
  "enquiry_id": "ENQ-ABCD1234",
  "lock_id": "LCK-XYZ78910",
  "payment_link_id": "PYL-MNOP5678",
  "payment_url": "https://payments.cashfree.com/forms/...",
  "amount": 400,
  "currency": "INR",
  "expires_at": "2026-02-28T12:48:00Z",
  "whatsapp_queued": true,
  "message": "Payment link generated and WhatsApp notification queued."
}`}
        note="WhatsApp is sent asynchronously — whatsapp_queued: true means it was enqueued, not necessarily delivered yet. Check the Dashboard tab for WABA status."
      />

      <div className="flex items-center gap-2 text-sm font-semibold text-slate-600 pt-2">
        <Zap className="w-4 h-4" />
        <span>Webhooks &amp; Admin</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <EndpointCard
          method="POST" path="/api/voice/payment-webhook"
          title="Cashfree Payment Webhook"
          desc="Configure this URL in Cashfree dashboard. Verifies payment, converts lock to confirmed booking in the main bookings collection."
          note="Only processes orders with order_id starting with VOICE-. Other webhooks are silently ignored."
        />
        <EndpointCard
          method="GET" path="/api/voice/admin/dashboard"
          title="Admin Dashboard (auth required)"
          desc="Returns real-time stats and last 20 enquiries, 10 locks, 10 payment links. Triggers expiry cleanup."
          note="Requires admin Bearer token."
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <EndpointCard
          method="GET" path="/api/voice/enquiries"
          title="List Enquiries (auth required)"
          desc="Optional ?status=active|expired|locked|consumed filter. Returns last 50 by default."
        />
        <EndpointCard
          method="GET" path="/api/voice/enquiries/{enquiry_id}"
          title="Get Single Enquiry (auth required)"
          desc="Returns full enquiry details including is_valid flag."
        />
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function VoiceApiPage() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading]     = useState(false);
  const token = localStorage.getItem('token');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/voice/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(r.data);
    } catch {
      toast.error('Failed to load Voice API dashboard');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  return (
    <div className="p-6 space-y-6" data-testid="voice-api-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow">
          <Mic className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Voice AI API</h1>
          <p className="text-sm text-slate-500">Real-time availability, slot locking, Cashfree payment links, WABA delivery</p>
        </div>
        <div className="ml-auto">
          <Badge variant="outline" className="text-emerald-600 border-emerald-300 bg-emerald-50 text-xs px-3 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5 inline-block animate-pulse" />
            Live
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="dashboard">
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="settings"  data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="api-keys"  data-testid="tab-api-keys">API Keys</TabsTrigger>
          <TabsTrigger value="api-docs"  data-testid="tab-api-docs">API Reference</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab data={dashboard} loading={loading} onRefresh={loadDashboard} />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab token={token} />
        </TabsContent>

        <TabsContent value="api-keys">
          <ApiKeysTab />
        </TabsContent>

        <TabsContent value="api-docs">
          <ApiReferenceTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
